import { TIERS } from "../game/tiers";
import { shade } from "./colors";

/**
 * Build the shared `<defs>` block: per-tier radial gradients plus filters and
 * special gradients used by the celestial tile art. Pure SVG — no raster
 * assets (spec §1).
 */
export function buildDefs(): string {
  const gradients = TIERS.map((tr) => {
    const bright = shade(tr.color, 0.6);
    const mid = tr.color;
    const dark = shade(tr.color, -0.5);
    return `
      <radialGradient id="grad-${tr.level}" cx="38%" cy="32%" r="72%">
        <stop offset="0%" stop-color="${bright}"/>
        <stop offset="45%" stop-color="${mid}"/>
        <stop offset="100%" stop-color="${dark}"/>
      </radialGradient>`;
  }).join("");

  // Accretion-disc ring (black hole) and a bright core gradient (stars).
  const special = `
    <radialGradient id="core-white" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="#ffffff"/>
      <stop offset="40%" stop-color="#fff7d6"/>
      <stop offset="100%" stop-color="#ffae3b" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="accretion" cx="50%" cy="50%" r="50%">
      <stop offset="55%" stop-color="#000000"/>
      <stop offset="62%" stop-color="#ff8a3b"/>
      <stop offset="74%" stop-color="#ffd54a"/>
      <stop offset="88%" stop-color="#7b3bff" stop-opacity="0.7"/>
      <stop offset="100%" stop-color="#7b3bff" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="nebula" cx="42%" cy="38%" r="68%">
      <stop offset="0%" stop-color="#fff7ff"/>
      <stop offset="35%" stop-color="#ff5fd0"/>
      <stop offset="65%" stop-color="#5b8cff"/>
      <stop offset="100%" stop-color="#2a1466"/>
    </radialGradient>
    <radialGradient id="halo" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="#ffffff" stop-opacity="0.9"/>
      <stop offset="100%" stop-color="#ffffff" stop-opacity="0"/>
    </radialGradient>
    <linearGradient id="beam" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#ffffff" stop-opacity="0"/>
      <stop offset="50%" stop-color="#bfe6ff" stop-opacity="0.9"/>
      <stop offset="100%" stop-color="#ffffff" stop-opacity="0"/>
    </linearGradient>`;

  // Diagonal hatch for blocked/obstacle cells.
  const patterns = `
    <pattern id="blocked-hatch" width="12" height="12" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
      <rect width="12" height="12" fill="#191b33"/>
      <rect width="6" height="12" fill="#33375e"/>
    </pattern>`;

  const filters = `
    <filter id="tile-glow" x="-60%" y="-60%" width="220%" height="220%">
      <feGaussianBlur in="SourceGraphic" stdDeviation="2.2" result="blur"/>
      <feMerge>
        <feMergeNode in="blur"/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>
    <filter id="tile-glow-strong" x="-80%" y="-80%" width="260%" height="260%">
      <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur"/>
      <feMerge>
        <feMergeNode in="blur"/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>
    <filter id="rocky" x="-20%" y="-20%" width="140%" height="140%">
      <feTurbulence type="fractalNoise" baseFrequency="0.09" numOctaves="3" seed="7" result="n"/>
      <feDisplacementMap in="SourceGraphic" in2="n" scale="6"/>
    </filter>`;

  return `<defs>${gradients}${special}${patterns}${filters}</defs>`;
}
